class StickyHeader extends HTMLElement {
    constructor() {
      super();
      this.megamenuLinks = this.querySelectorAll('.navigation_item_holder_megamenu')
    }

    connectedCallback() {
      this.updateLocalStorage();

      this.showCurrentLinks();

      this.header = document.querySelector('.section-header');
      this.headerIsAlwaysSticky = this.getAttribute('data-sticky-type') === 'always' || this.getAttribute('data-sticky-type') === 'reduce-logo-size';
      this.headerBounds = {};
      this.allMobileNavLinks = this.querySelectorAll('.menu_mobile_click');
      this.menuOpener = this.querySelector('.mobile_navigation_opener');
      this.searchButton = this.querySelector('.navigation_icon_search');
      this.searchButtonClose = this.querySelector('.navigation_icon_search_close');
      this.search = this.querySelector('predictive-search');

      this.setHeaderHeight();

      window.matchMedia('(max-width: 990px)').addEventListener('change', this.setHeaderHeight.bind(this));
      this.menuOpener?.addEventListener('click', (e) => this.openMenu(e));
      this.searchButton?.addEventListener('click', (e) => this.openSearch(e));
      this.searchButtonClose?.addEventListener('click', (e) => this.closeSearch(e));

      this.allMobileNavLinks.forEach(link => {
        link.addEventListener('click', (e) => this.clickMobileNavItem(e));
      });

      if (this.headerIsAlwaysSticky) {
        this.header.classList.add('shopify-section-header-sticky');
      };

      this.currentScrollTop = 0;
      this.preventReveal = false;
      this.predictiveSearch = this.querySelector('predictive-search');

      this.onScrollHandler = this.onScroll.bind(this);
      this.hideHeaderOnScrollUp = () => this.preventReveal = true;

      this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
      window.addEventListener('scroll', this.onScrollHandler, false);

      this.setSubmenuWidths();

      window.addEventListener('resize', this.debouncedSetSubmenuWidths.bind(this));

      this.createObserver();
    }

    debouncedSetSubmenuWidths(){
      let resizeTimeout;
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => this.setSubmenuWidths(), 400);
    }

    setSubmenuWidths(){
      if (window.innerWidth >= 990) {
        this.megamenuLinks.forEach(link => {
          const submenu = link.querySelector('.navigation_item_megamenu_submenus');
          const submenuHolder = link.querySelector('.navigation_item_megamenu_holder');

          if (!submenu || !submenuHolder) return;

          const positionSubmenu = () => {
            const submenuWidth = submenu.scrollWidth;
            const menuRect = link.getBoundingClientRect();
            const windowWidth = window.innerWidth;
            
            const rightSpace = windowWidth - menuRect.x;
            const isLeftSide = menuRect.x <= windowWidth / 2;
            
            let leftPosition;

            if (isLeftSide) {
              leftPosition = submenuWidth <= rightSpace
                ? menuRect.x  
                : (windowWidth - submenuWidth) / 2; 
            } else {
              
              leftPosition = submenuWidth <= menuRect.x
                ? menuRect.x - submenuWidth + menuRect.width 
                : (windowWidth - submenuWidth) / 2; 
            }

            submenuHolder.style.left = `${leftPosition}px`;
            submenu.setAttribute('data-width', submenuWidth);
          };

          const loadImages = () => {
            const images = Array.from(submenu.querySelectorAll('img'));
            
            if (images.length === 0) {
              positionSubmenu();
              return;
            }

            const imagePromises = images.map(img => 
              new Promise(resolve => {
                if (img.complete) {
                  resolve();
                } else {
                  img.addEventListener('load', resolve);
                  img.addEventListener('error', resolve);
                }
              })
            );

            Promise.all(imagePromises).then(positionSubmenu);
          };

          loadImages();
        });

        document.querySelector('body').style.overflowX = 'hidden';
      }
    }

    setHeaderHeight() {
      document.documentElement.style.setProperty('--header-height', `${this.header.offsetHeight}px`);
      document.body.style.setProperty('--header-height', `${this.header.offsetHeight}px`);
    }

    disconnectedCallback() {
      this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
      window.removeEventListener('scroll', this.onScrollHandler);
      window.removeEventListener('resize', this.debouncedSetSubmenuWidths.bind(this));

      this.menuOpener.removeEventListener('click', (e) => this.openMenu(e));

      this.allMobileNavLinks.forEach(link => {
        link.removeEventListener('click', (e) => this.clickMobileNavItem(e));
      });
    }

    createObserver() {
      let observer = new IntersectionObserver((entries, observer) => {
        this.headerBounds = entries[0].intersectionRect;
        observer.disconnect();
      });

      observer.observe(this.header);
    }

    onScroll() {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

      if (this.predictiveSearch && this.predictiveSearch.isOpen) return;

      if (scrollTop > this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
        this.header.classList.add('scrolled-past-header');
        if (this.preventHide) return;
        requestAnimationFrame(this.hide.bind(this,null));
      } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
        this.header.classList.add('scrolled-past-header');
        if (!this.preventReveal) {
          requestAnimationFrame(this.reveal.bind(this,null));
        } else {
          window.clearTimeout(this.isScrolling);

          this.isScrolling = setTimeout(() => {
            this.preventReveal = false;
          }, 66);

          requestAnimationFrame(this.hide.bind(this,null));
        }
      } else if (scrollTop <= this.headerBounds.top) {
        this.header.classList.remove('scrolled-past-header');
        requestAnimationFrame(this.reset.bind(this));
      }

      this.currentScrollTop = scrollTop;
    }

    hide(forced = false) {
      if (!forced && this.headerIsAlwaysSticky) return;
      this.header.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
      this.closeMenuDisclosure();
      this.closeSearchModal();
    }

    reveal(forced = false) {
      if (!forced && this.headerIsAlwaysSticky) return;
      this.header.classList.add('shopify-section-header-sticky', 'animate');
      this.header.classList.remove('shopify-section-header-hidden');
    }

    reset() {
      if (this.headerIsAlwaysSticky) return;
      this.header.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate');
    }

    closeMenuDisclosure() {
      this.disclosures = this.disclosures || this.header.querySelectorAll('header-menu');
      this.disclosures.forEach(disclosure => disclosure.close());
    }

    closeSearchModal() {
      this.searchModal = this.searchModal || this.header.querySelector('details-modal');
      this.searchModal?.close(false);
    }

    clickMobileNavItem(e){
      if(window.innerWidth < 990){
        let link = e.currentTarget;
        if(!link.closest('.navigation_item_holder_dropdown') && !link.closest('.navigation_item_holder_megamenu')) return;
        e.preventDefault();

        const holder = link.nextElementSibling;

        if(holder && link.classList.contains('active')) {
          link.classList.remove('active');
          holder.style.height = '0px';

            holder.querySelectorAll('.navigation_item_link_list_item').forEach((item, index) => {
              item.style.transform = `translateY(12px)`;
            })
          
          return;
        }

        if(holder && holder.classList.contains('navigation_item_megamenu_holder')) {
          const childContainer = holder.querySelector('.navigation_item_megamenu_submenus');
          holder.style.height = childContainer.offsetHeight + 'px';
          link.classList.add('active');

            holder.querySelectorAll('.navigation_item_link_list_item').forEach((item, index) => {
              item.style.transitionDelay = `${index * 0.02}s`;
              item.style.transform = `translateY(0px)`;
            })
        }
      }
    }

    openMenu(e){
      e.preventDefault();
      let button = e.target;

      if(button.classList.contains('mobile_menu_open')){
        button.classList.remove('mobile_menu_open')
        document.body.classList.remove('mobile_menu_open')
        document.body.classList.remove('menu_open')
        document.querySelector('.menu_mobile_open.active')?.classList.remove('active');
      }
      else{
        button.classList.add('mobile_menu_open')
        document.body.classList.add('menu_open')
        document.body.classList.add('mobile_menu_open')
      }
    }

    openSearch(e){
      e.preventDefault();

        this.search.classList.add('active');
        this.search.querySelector('.search__input')?.focus();
        if(window.innerWidth < 990) document.body.classList.add('menu_open')
    }

    closeSearch(e){
      e.preventDefault();

      this.search.classList.remove('active');
      document.body.classList.remove('menu_open')
    }

    updateLocalStorage() {
      const pageType = localStorage.getItem('pageType');
      const activeLink = document.querySelector('.landing_link.active');

      if(activeLink) {
        activeLink.classList.remove('active');
      }
      
      if(!pageType && this.dataset.pageType == 'general') {
        localStorage.setItem('pageType', 'women');

        const womenLink = document.querySelector('.landing_link[data-title="women"]');
        if(womenLink) {
          womenLink.classList.add('active');
        }
      } else if (this.dataset.pageType != 'general') {
        localStorage.setItem('pageType', this.dataset.pageType);

        const newActive = document.querySelector(`.landing_link[data-title="${this.dataset.pageType}"]`);
        if(newActive) {
          newActive.classList.add('active');
        }
      } else {
        const newPageType = localStorage.getItem('pageType');
        const newActive = document.querySelector(`.landing_link[data-title="${newPageType}"]`);
        if(newActive) {
          newActive.classList.add('active');
        }
      }
    }

    showCurrentLinks() {
      const pageType = localStorage.getItem('pageType');

      this.querySelectorAll('.navigation_item_holder').forEach(item => {
        if(item.hasAttribute(`${pageType}-enabled`)) {
          item.style.display = 'block';
        } else {
          item.style.display = 'none';
          item.remove();
        }
      })
    }
  }

  if (!window.customElements.get("sticky-header")) {
    customElements.define('sticky-header', StickyHeader);
  }